<template>
  <Modal ref="modal" class="modal-branch-createdit" :actions="actions" @action="handleAction" immediate>
    <h1>{{ $t(`branches.modals.${mode}.title`) }}</h1>
    <form>
      <div class="group">
        <TextInput
          fill
          label="SPD Name"
          error-message="SPD Name ist ein Pflichtfeld"
          :vuelidate="$v.form.name"
          :value.sync="form.name"
        />
      </div>
      <div class="group">
        <Select
          fill
          label="Bezirk"
          placeholder="-- Auswählen --"
          :options="districts"
          error-message="Bezirk ist ein Pflichtfeld"
          :vuelidate="$v.form.district"
          :value.sync="form.district"
        />
      </div>
      <div class="group" v-if="form.district === OUT_OF_CANTON">
        <TextInput
          fill
          label="Kanton"
          error-message="Kanton ist ein Pflichtfeld"
          :vuelidate="$v.form.state"
          :value.sync="form.state"
        />
      </div>
      <div class="group">
        <TextInput
          fill
          label="Adresse"
          error-message="Adresse ist ein Pflichtfeld"
          :vuelidate="$v.form.address"
          :value.sync="form.address"
        />
      </div>
      <div class="group">
        <TextInput
          fill
          label="Ort"
          error-message="Ort ist ein Pflichtfeld"
          :vuelidate="$v.form.city"
          :value.sync="form.city"
        />
      </div>
      <div class="group">
        <TextInput
          fill
          label="Telefon"
          :value.sync="form.phone"
        />
      </div>
      <div class="group">
        <TextInput
          fill
          type="email"
          label="E-Mail"
          :value.sync="form.email"
        />
      </div>
      <div class="group">
        <TextInput
          fill
          label="Webseite"
          :value.sync="form.url"
        />
      </div>
      <div class="group">
        <FileInput
          label="Frontseite"
          :max-files="1"
          hint="Bild muss 1500 x 1420 Pixel gross sein"
          aspect-ratio="1.06/1"
          accept="jpe?g|png|gif|webp"
          :value.sync="form.logo"
        />
      </div>
    </form>
  </Modal>
</template>

<script>
  import { required } from 'vuelidate/lib/validators';
  import Modal from '@/components/ui/Modal';
  import TextInput from '@/components/ui/inputs/TextInput';
  import TextareaInput from '@/components/ui/inputs/TextareaInput';
  import Select from '@/components/ui/inputs/Select';
  import FileInput from '@/components/ui/inputs/FileInput';

  const OUT_OF_CANTON = 'Ausserkantonal';

  export default {
    name: 'ModalBranchCreateEdit',
    components: {
      FileInput,
      Select,
      TextareaInput,
      TextInput,
      Modal,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      uuid: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        mode: this.uuid ? 'edit' : 'create',
        form: {
          name: null,
          district: null,
          state: null,
          address: null,
          city: null,
          phone: null,
          email: null,
          url: null,
          logo: null,
        },
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      actions() {
        const i18n = this.$i18n;
        const out = [{ value: 'cancel', label: i18n.t('labels.cancel') }];
        if (this.uuid) {
          out.push({ value: 'update', label: i18n.t('labels.save') });
        } else {
          out.push({ value: 'save', label: i18n.t('labels.save') });
        }
        return out;
      },
      OUT_OF_CANTON() {
        return OUT_OF_CANTON;
      },
      districts() {
        return [
          { label: 'Affoltern', value: 'Affoltern' },
          { label: 'Andelfingen', value: 'Andelfingen' },
          { label: 'Bülach', value: 'Bülach' },
          { label: 'Dielsdorf', value: 'Dielsdorf' },
          { label: 'Dietikon', value: 'Dietikon' },
          { label: 'Hinwil', value: 'Hinwil' },
          { label: 'Horgen', value: 'Horgen' },
          { label: 'Meilen', value: 'Meilen' },
          { label: 'Pfäffikon', value: 'Pfäffikon' },
          { label: 'Uster', value: 'Uster' },
          { label: 'Winterthur', value: 'Winterthur' },
          { label: 'Zürich', value: 'Zürich' },
          { label: 'Ausserkantonal', value: OUT_OF_CANTON },
        ];
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      'form.district': {
        handler(value) {
          if (value === OUT_OF_CANTON) {
            this.form.state = null;
          } else {
            this.form.state = 'Zürich';
          }
        },
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    validations: {
      form: {
        name: { required },
        state: { required },
        district: { required },
        address: { required },
        city: { required },
      },
    },
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    async created() {
      const uuid = this.uuid;
      if (uuid) {
        const branch = await this.$sav.api.get(`/branch/${uuid}`);
        const logo = branch?.logo ? [{ base64: branch.logo }] : [];
        this.form = {
          name: branch?.name,
          district: branch?.district,
          state: branch?.state,
          address: branch?.address,
          city: branch?.city,
          phone: branch?.phone,
          email: branch?.email,
          url: branch?.url,
          logo,
        };
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // async mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      //----------------------------------
      // Event Handlers
      //----------------------------------
      async handleAction(action) {
        if (['save', 'update'].includes(action)) {
          const $v = this.$v;
          $v.$touch();
          if (!$v.$invalid) {
            const uuid = this.uuid;
            const form = Object.assign({}, this.form);

            if (form.logo && form.logo.length > 0) {
              form.logo = form.logo[0].base64;
            }

            let response;
            if (uuid) {
              response = await this.$sav.api.patch(`/branch/${uuid}`, null, form);
            } else {
              response = await this.$sav.api.post('/branch', null, form);
            }
            if (response) {
              this.$emit('done');
            }
            this.$refs.modal.close();
          }
        } else {
          this.$refs.modal.close();
        }
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.modal-branch-createdit {
  h1 {
    margin-bottom: 12px;
  }

  form {
    padding: 24px 0;

    .group {
      margin-bottom: 24px;
      &:last-of-type {
        margin-bottom: 0;
      }
    }

    hr {
      border: 0;
      margin: 24px 0;
      border-bottom: 1px solid $color-black-alpha-10;
    }
  }
}
</style>
